import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/Button";
import { useI18n, useT } from "@/contexts/I18nContext";
import { useToast } from "@/contexts/ToastContext";
import { call, CALLABLES } from "@/services/callables";
import type { CommitImportResult, ImportedPerson } from "@/types";

/**
 * Step four: the passwords.
 *
 * **They are shown once.** Each exists in the printed PDF and in the Auth
 * password hash, and nowhere else — not in Firestore, not in a log, not
 * anywhere we could look them up if asked. That is the whole point, and it is
 * also why the warning here is the loudest thing on the page rather than a note
 * at the bottom.
 *
 * **Only staff have any.** A player holds a device the institution enrolled and
 * never signs in, so an import of a class of thirty produces no slips at all —
 * and this step says so rather than opening an empty PDF.
 */
export function CredentialsStep({ result }: { result: CommitImportResult }) {
  const t = useT();
  const { locale, plural } = useI18n();
  const { success, showError } = useToast();
  const [busy, setBusy] = useState(false);
  const [printed, setPrinted] = useState(false);

  /*
    The people who have something to print.

    Filtered here rather than in the callable, because the count in the heading
    and the button that opens the PDF have to agree with each other — and with
    what somebody is about to hold in their hand.
  */
  const withCredentials = result.created.filter(
    (person): person is ImportedPerson & { credentials: NonNullable<ImportedPerson["credentials"]> } =>
      person.credentials !== undefined,
  );

  const print = async () => {
    setBusy(true);
    try {
      const response = await call<
        {
          records: Array<{
            displayName: string;
            username: string;
            temporaryPassword: string;
            groupName?: string;
          }>;
          locale: string;
        },
        { url: string }
      >(CALLABLES.renderCredentialSheets, {
        records: withCredentials.map((person) => ({
          displayName: person.displayName,
          username: person.credentials.username,
          temporaryPassword: person.credentials.temporaryPassword,
          ...(person.groupName ? { groupName: person.groupName } : {}),
        })),
        locale,
      });
      window.open(response.url, "_blank", "noopener");
      setPrinted(true);
      success("import.credentials.opened");
    } catch (error) {
      showError(error);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-2xl">
      <h2 className="section-title">
        {t("import.credentials.title", { count: result.created.length })}
      </h2>

      <ul className="mt-3 flex flex-col gap-1 text-sm text-ink-muted">
        {result.groupsCreated.length > 0 ? (
          <li>
            {plural(result.groupsCreated.length, {
              one: t("import.credentials.groupsCreated.one", {
                count: result.groupsCreated.length,
              }),
              other: t("import.credentials.groupsCreated.other", {
                count: result.groupsCreated.length,
              }),
            })}
          </li>
        ) : null}
        {result.skipped.length > 0 ? (
          <li>{t("import.credentials.skipped", { count: result.skipped.length })}</li>
        ) : null}
      </ul>

      {result.skipped.length > 0 ? (
        <ul className="mt-3 flex flex-col gap-1 text-sm text-ink-soft">
          {result.skipped.map((skip) => (
            <li key={skip.row}>
              <span className="text-ink-faint">{skip.row}</span> {t(skip.reasonKey)}
            </li>
          ))}
        </ul>
      ) : null}

      {withCredentials.length === 0 ? (
        /*
          A class of players produces no slips at all.

          Saying so is the whole of this branch: an owner who expected paper and
          got a silent page would go looking for it, and there is nothing to
          find.
        */
        <>
          <div className="surface-inset mt-8 px-5 py-4">
            <p className="text-ink">{t("import.credentials.noneNeeded")}</p>
            <p className="mt-2 text-sm text-ink-muted">
              {t("import.credentials.noneNeededWhy")}
            </p>
          </div>

          <div className="mt-6">
            <Link
              to="/people"
              className="text-sm text-ink-muted underline underline-offset-2 hover:text-ink"
            >
              {t("import.credentials.done")}
            </Link>
          </div>
        </>
      ) : (
        <>
          {/* The loudest thing on the page, because it is the only irreversible one. */}
          <div className="mt-8 rounded-card border-2 border-danger bg-danger-soft px-5 py-4">
            <p className="text-ink">{t("import.credentials.warning")}</p>
            <p className="mt-2 text-sm text-ink">
              {t("import.credentials.staffOnly", { count: withCredentials.length })}
            </p>
          </div>

          <div className="mt-6 flex flex-wrap items-center gap-3">
            <Button busy={busy} onClick={print}>
              {busy ? t("import.credentials.printing") : t("import.credentials.print")}
            </Button>
            {printed ? (
              <Link
                to="/people"
                className="text-sm text-ink-muted underline underline-offset-2 hover:text-ink"
              >
                {t("import.credentials.done")}
              </Link>
            ) : null}
          </div>

          {printed ? (
            <p className="mt-3 text-sm text-ink-muted">{t("import.credentials.reprint")}</p>
          ) : null}
        </>
      )}
    </div>
  );
}
